import morph from "../helpers/morph";

// Keep Searchspring recommendations stable across PDP variant swaps (preserve targets, prevent dupes, re-hydrate).
const SS_SECTION_SELECTOR = ".ss-recommendations-target";
const SS_TARGET_SELECTOR = '[class*="ss__recs__"]';
const SS_MOUNT_SELECTOR = "[data-ss-recs-mount]";

const SS_CONTEXT_ID = "searchspring-context";
const SS_CONTEXT_SRC_MATCH = /snapui\.searchspring\.io/i;

const SS_RECS_CONFIG_SELECTOR = 'script[type="searchspring/recommendations"]';

function dedupeRecsConfigScripts() {
  const scripts = [...document.querySelectorAll(SS_RECS_CONFIG_SELECTOR)];
  if (scripts.length <= 1) return;
  scripts.slice(0, -1).forEach((s) => s.parentNode?.removeChild(s));
}

function extractSearchspringTargets(root) {
  const wrapper = root?.querySelector(SS_SECTION_SELECTOR);
  if (!wrapper) return [];
  const targets = [...wrapper.querySelectorAll(SS_TARGET_SELECTOR)];
  targets.forEach((n) => n.parentNode?.removeChild(n));
  return targets;
}

function restoreSearchspringTargets(root, targets) {
  if (!root || !targets?.length) return;
  const wrapper = root.querySelector(SS_SECTION_SELECTOR);
  if (!wrapper) return;
  const mount = wrapper.querySelector(SS_MOUNT_SELECTOR) || wrapper;
  targets.forEach((node) => mount.appendChild(node));
}

function normalizeSearchspringDom(scope = document) {
  const wrappers = [...(scope?.querySelectorAll(SS_SECTION_SELECTOR) || [])];
  if (!wrappers.length) return;

  const primary = wrappers[0];

  wrappers.slice(1).forEach((w) => {
    [...w.querySelectorAll(SS_TARGET_SELECTOR)].forEach((t) => primary.appendChild(t));
    w.remove();
  });

  [...scope.querySelectorAll(SS_TARGET_SELECTOR)].forEach((t) => {
    if (!primary.contains(t)) primary.appendChild(t);
  });

  const seenIds = new Set();
  [...primary.querySelectorAll(SS_TARGET_SELECTOR)].forEach((t) => {
    const id = t.getAttribute("id");
    if (!id) return;
    if (seenIds.has(id)) t.remove();
    else seenIds.add(id);
  });

  const seenSig = new Set();
  [...primary.querySelectorAll(SS_TARGET_SELECTOR)].forEach((t) => {
    if (t.getAttribute("id")) return;

    const sig = [...t.classList].filter((c) => c.includes("ss__recs__")).sort().join("|");
    if (!sig) return;

    if (seenSig.has(sig) && t.innerHTML.trim() === "") t.remove();
    else seenSig.add(sig);
  });
}

function trySearchspringRecsApiRefresh() {
  const ss = window.searchspring || window.Searchspring || window.ss;
  if (!ss) return false;

  const candidates = [
    ss?.recommendations?.refresh,
    ss?.recommendations?.reload,
    ss?.recommendations?.render,
    ss?.recommendations?.init,
    ss?.recs?.refresh,
    ss?.recs?.reload,
    ss?.recs?.render,
    ss?.recs?.init,
    ss?.refresh,
    ss?.reload,
    ss?.render,
    ss?.init,
  ].filter((fn) => typeof fn === "function");

  for (const fn of candidates) {
    try {
      fn.call(ss?.recommendations || ss?.recs || ss);
      return true;
    } catch (_) {}
  }

  return false;
}

function getContextScript() {
  const el = document.getElementById(SS_CONTEXT_ID);
  if (!(el instanceof HTMLScriptElement)) return null;
  const src = el.getAttribute("src") || "";
  if (!SS_CONTEXT_SRC_MATCH.test(src)) return null;
  return el;
}

async function hardResetSearchspringContext() {
  const existing = getContextScript();
  if (!existing) return;

  const src = existing.getAttribute("src") || "";
  const defer = existing.hasAttribute("defer");
  const asyncAttr = existing.hasAttribute("async");
  const inlineConfig = existing.textContent || "";

  try {
    const ss = window.searchspring || window.Searchspring || window.ss;
    ss?.recommendations?.destroy?.();
    ss?.recs?.destroy?.();
  } catch (_) {}

  existing.parentNode?.removeChild(existing);

  const s = document.createElement("script");
  s.id = SS_CONTEXT_ID;
  s.src = `${src}${src.includes("?") ? "&" : "?"}ts=${Date.now()}`;
  if (defer) s.defer = true;
  if (asyncAttr) s.async = true;
  s.textContent = inlineConfig;

  await new Promise((resolve, reject) => {
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

let ssRefreshInFlight = false;

async function triggerSearchspringRecsRefresh() {
  if (ssRefreshInFlight) return;
  ssRefreshInFlight = true;

  try {
    if (trySearchspringRecsApiRefresh()) return;
    await hardResetSearchspringContext();
    trySearchspringRecsApiRefresh();
  } catch (e) {
    console.warn("[Searchspring] rec refresh failed", e);
  } finally {
    ssRefreshInFlight = false;
  }
}

const MAIN_CONTENT_SELECTOR = "#MainContent";
const sectionCache = new Map();

function getSectionIds() {
  return [...document.querySelectorAll(`${MAIN_CONTENT_SELECTOR} [id^="shopify-section"]`)].map((node) =>
    node.id.replace("shopify-section-", ""),
  );
}

function warmMedia(html) {
  if (!html) return;

  const doc = new DOMParser().parseFromString(html, "text/html");
  const images = [...doc.querySelectorAll("img:not(.product-form__swatch-image)")].slice(0, 2);

  images.forEach((img) => {
    const preload = new Image();
    if (img.sizes) preload.sizes = img.sizes;
    if (img.srcset) preload.srcset = img.srcset;
    if (img.src) preload.src = img.src;
  });
}

function fetchSections(productUrl, sectionIds) {
  const url = new URL(productUrl, window.location.origin);
  const key = url.pathname;

  if (sectionCache.has(key)) return sectionCache.get(key);

  url.searchParams.set("sections", sectionIds.join(","));

  const request = window.theme
    .fetch(url)
    .then((res) => res.json())
    .then((sections) => {
      warmMedia(sections[sectionIds[0]]);
      return sections;
    })
    .catch((e) => {
      sectionCache.delete(key);
      throw e;
    });

  sectionCache.set(key, request);
  return request;
}

class PdpVariantButton extends HTMLElement {
  constructor() {
    super();
    this.linkEl = null;
    this.handleLinkClick = null;
    this.handlePrefetch = null;
  }

  connectedCallback() {
    this.linkEl = this.closest("[data-variant-link]");
    if (!this.linkEl) return;

    this.handleLinkClick = (e) => {
      e.preventDefault();
      this.markSelected();
      this.selectVariant();
    };

    this.handlePrefetch = () => {
      const sectionIds = getSectionIds();
      if (!sectionIds.length) return;
      fetchSections(this.getAttribute("product-url"), sectionIds).catch(() => {});
    };

    this.linkEl.addEventListener("click", this.handleLinkClick);
    this.linkEl.addEventListener("pointerdown", this.handlePrefetch, { passive: true });
  }

  disconnectedCallback() {
    this.linkEl?.removeEventListener("click", this.handleLinkClick);
    this.linkEl?.removeEventListener("pointerdown", this.handlePrefetch);
  }

  markSelected() {
    const SELECTED_CLASS = "product-form__swatch-option-wrapper--selected";
    const group = this.closest(".product-form__option-values--swatch");
    if (!group) return;

    group.querySelectorAll(`.${SELECTED_CLASS}`).forEach((el) => el.classList.remove(SELECTED_CLASS));
    this.querySelector(".product-form__swatch-option-wrapper")?.classList.add(SELECTED_CLASS);
  }

  async selectVariant() {
    const url = new URL(this.getAttribute("product-url"), window.location.origin);

    const currentRoot = this.closest(MAIN_CONTENT_SELECTOR);
    if (!currentRoot) return;

    const sectionIds = getSectionIds();
    if (!sectionIds.length) return;

    const preservedTargets = extractSearchspringTargets(currentRoot);

    try {
      const sections = await fetchSections(url, sectionIds);

      if (!window.Shopify.designMode && this.updateUrl) {
        window.theme.replaceState("", "", url);
      }

      sectionIds.forEach((id) => {
        const html = sections[id];
        if (!html) return;

        const liveSection = document.getElementById(`shopify-section-${id}`);
        if (!liveSection) return;

        morph(liveSection, document.createRange().createContextualFragment(html));
      });

      dedupeRecsConfigScripts();
      restoreSearchspringTargets(currentRoot, preservedTargets);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => normalizeSearchspringDom(currentRoot));
      });

      preservedTargets.forEach((t) => {
        if (t) t.innerHTML = "";
      });

      await triggerSearchspringRecsRefresh();
    } catch (e) {
      console.warn("[PDP] variant swap failed", e);
    }
  }

  get updateUrl() {
    return !this.hasAttribute("disable-update-url");
  }
}

if (!customElements.get("pdp-variant-button")) {
  customElements.define("pdp-variant-button", PdpVariantButton);
}

window.addEventListener("DOMContentLoaded", () => normalizeSearchspringDom(document));